export * from "./$__metadata.js";
export * from "./$__needle.types.js"


// TODO: move proxy url to environment config

// Used for AI chat to proxy requests that don't have an AI token (e.g. chrome extension)
export const needleCloudProxy = "https://proxy.needle.run/";


export const needleUserAgent = "Needle Inspector/1.0 (+https://needle.tools/)";


const editorUIElements = new WeakSet<object>();

type ElementWithParent = Element & { parentElement: ElementWithParent | null };
function isElementWithParent(element: HTMLElement | EventTarget | null): element is ElementWithParent {
    // Popout-created elements have a different Element constructor.
    return element !== null && (element as Node).nodeType === 1 && "parentElement" in element;
}

/** True if THIS element is an editor UI root (no ancestor walk). */
export function isNeedleEditorRootElement(element: Element | EventTarget | null): boolean {
    if (!element) return false;
    if (editorUIElements.has(element)) return true;
    if (!isElementWithParent(element)) return false;
    if (element.id === "NEEDLE_EDITOR_ROOT") return true;
    return element.tagName.startsWith("NEEDLE-EDITOR-");
}

/**
 * The next element UP, crossing shadow boundaries.
 *
 * `parentElement` alone dead-ends at a shadow root (a ShadowRoot is not an
 * Element, so its children have a null parentElement) — which meant EVERY
 * element inside the editor's shadow DOM read as "not editor UI". The guards
 * built on this then treated menu/panel input as viewport input: the fly
 * controls swallowed clicks on menu items and the wheel over panels, and
 * picking deselected on a UI click.
 */
function parentOf(element: ElementWithParent): Element | null {
    if (element.parentElement) return element.parentElement;
    const root = element.getRootNode?.();
    const host = (root as ShadowRoot | null)?.host ?? null;
    return isElementWithParent(host) ? host : null;
}

export function isNeedleEditorHTMLElement(element: Element | EventTarget | null): boolean {

    if (!element) return false;
    if (editorUIElements.has(element)) return true;

    if (!isElementWithParent(element)) {
        return false;
    }

    // console.log("CHECKING ELEMENT:", element, element.id, element.tagName);

    if (isNeedleEditorRootElement(element)) return true;

    const parent = parentOf(element);
    if (parent) {
        const res = isNeedleEditorHTMLElement(parent);
        if (res === true) {
            editorUIElements.add(element);
            return true;
        }
    }
    return false

}

/**
 * Whether an EVENT originated in editor UI, decided from its composed path.
 *
 * Preferred over testing `event.target`: that single node depends on shadow
 * retargeting (a window listener sees the HOST element, a listener inside the
 * shadow tree sees the inner one), so the same click reads differently
 * depending on where the listener sits. The composed path contains the real
 * innermost node AND every shadow host above it, so the answer is the same
 * either way.
 */
export function isNeedleEditorEvent(event: Event): boolean {
    const path = event.composedPath?.();
    if (path?.length) return path.some(target => isNeedleEditorRootElement(target));
    return isNeedleEditorHTMLElement(event.target);
}
